/**
 * SmartTask V2 - useSmartTaskConfig Hook
 * 
 * Fetches and resolves SmartTask configuration from:
 * - /api/v1/work-items/smart-task-config (all templates + industry solution + client settings)
 * - /api/v1/operations/my-features (user's feature_settings including smart_task_template_key & display_mode)
 * 
 * Workflow:
 * 1. Fetch both endpoints in parallel
 * 2. Extract the user's assigned template_key from their feature_settings
 * 3. Find the matching template from workflow_templates[] array  
 * 4. Pass that template's config.smart_task + client settings + user features into resolveConfig()
 * 5. resolveConfig merges: defaults < template < user_features (priority order)
 * 
 * Returns a single, resolved SmartTaskConfig object.
 */

'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import { useOperations } from '@/contexts/OperationsContext';
import {
  fetchDemoSessionResponse,
  resolveOperationsDemoModuleKey,
} from '@/lib/demo/clientContext';
import type {
  DemoSessionInfo,
  DemoSourceConfiguration,
  DemoSourceIndustrySolution,
  DemoSourceWorkflowTemplate,
} from '@/lib/demo/types';
import { isStandardOperationsSolutionKey } from '@/lib/operations/specializedSolutions';
import { normalizeSmartTaskDisplayMode } from '@/lib/operations/smartTaskDisplayMode';
import {
  buildSmartTaskTemplateAssignment,
  getOperationsTeamSmartTaskSettings,
  isSmartTaskEnabledForTeam,
  parseSmartTaskTemplateKeys,
} from '@/lib/operations/teamConfig';
import { normalizeTemplateOrderKey } from '@/lib/operations/solutionTemplateOrder';
import { fetchOperationsMyFeatures } from '@/lib/operations/smartTaskBootstrap';
import { useEffectivePermissions } from '@/lib/iam/useEffectivePermissions';
import { overlaySolutionPermissions } from '@/lib/operations/actionPermissions';
import { PERMISSION_DEFINITIONS_BY_KEY } from '@/lib/iam/permissionDefinitions';
import {
  getSmartTaskRawConfigBundleSnapshot,
  getSmartTaskResolvedConfigSnapshot,
  getSmartTaskUserFeaturesSnapshot,
  saveSmartTaskRawConfigBundleSnapshot,
  saveSmartTaskResolvedConfigSnapshot,
  saveSmartTaskUserFeaturesSnapshot,
} from '@/lib/operations/smartTaskOfflineCache';
import { resolveConfig, getIndustryTerminology } from '../engine/configResolver';
import {
  writeSmartTaskColdStartLog,
} from '../utils/coldStartDiagnostics';
import type { SmartTaskConfig, SmartTaskSolutionOption } from '../types';
import { isCapacitor } from '@/lib/platformUtils';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import type { SmartTaskModalDefinition, SmartTaskShellConstants, SmartTaskStatCardsConfig, SmartTaskPanelSmartActionsConfig, SmartTaskDisplayModesConfig, SmartTaskUnifiedFilterTab } from '@/config/industry-solutions/types';
import { createLogger } from '@/lib/debugLogger';

const logger = createLogger('useSmartTaskConfig');

// ============================================================================
// LOCAL TYPES — Dynamic API/cache payload shapes
// ============================================================================

interface MyFeaturesApiResponse {
  success?: boolean;
  data?: Record<string, unknown>;
  feature_settings?: Record<string, unknown>;
  smart_task_template_keys?: string[];
  smart_task_template_key?: string;
  operations_role?: string;
  smart_task_industry_solution?: string;
  [key: string]: unknown;
}

/**
 * Config-only keys that my-features provides which are NOT permission keys.
 * These are extracted from my-features and overlaid on top of effective
 * permissions, since the IAM resolver doesn't carry config data.
 */
const CONFIG_ONLY_KEYS = new Set([
  'smart_task_template_keys',
  'smart_task_template_key',
  'smart_task_display_mode',
  'smart_task_industry_solution',
  'operations_role',
  'allow_appointment_detours',
]);

/**
 * Extract only config-specific keys from my-features data, filtering out
 * any keys that are defined in PERMISSION_DEFINITIONS (permission keys).
 * This ensures my-features only contributes config data (template keys,
 * display mode, industry solution) — not permission values, which come
 * from the unified IAM resolver.
 */
function extractConfigKeys(data: MyFeaturesApiResponse | null): Record<string, unknown> {
  if (!data) return {};
  const baseFeatures = (data?.data || data?.feature_settings || {}) as Record<string, unknown>;
  const templateAssignment = buildSmartTaskTemplateAssignment(
    data?.smart_task_template_keys
    || data?.smart_task_template_key
    || (baseFeatures.smart_task_template_keys as string[] | undefined)
    || (baseFeatures.smart_task_template_key as string | undefined)
    || null
  );

  const configKeys: Record<string, unknown> = {
    ...templateAssignment,
    operations_role: data?.operations_role || baseFeatures.operations_role || 'user',
    smart_task_industry_solution: data?.smart_task_industry_solution || baseFeatures.smart_task_industry_solution || null,
  };

  // Also carry through any base feature keys that are config-only
  for (const [key, value] of Object.entries(baseFeatures)) {
    if (CONFIG_ONLY_KEYS.has(key) && !PERMISSION_DEFINITIONS_BY_KEY.has(key)) {
      configKeys[key] = value;
    }
  }

  return configKeys;
}

/**
 * Extract config keys from a previously-built userFeatures record (e.g. from
 * the offline cache). Filters out any keys that are defined permission keys,
 * keeping only config-specific keys.
 */
function extractConfigKeysFromRecord(record: Record<string, unknown>): Record<string, unknown> {
  const configKeys: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (CONFIG_ONLY_KEYS.has(key) && !PERMISSION_DEFINITIONS_BY_KEY.has(key)) {
      configKeys[key] = value;
    }
  }
  return configKeys;
}

interface WorkflowTemplateApi {
  template_key?: string;
  solution_key?: string | null;
  name?: string;
  template_name?: string;
  description?: string;
  category?: string;
  icon?: string;
  color?: string;
  item_type?: string;
  document_types?: string[];
  hierarchy?: Record<string, unknown>;
  statuses?: Record<string, unknown>;
  smart_task?: Record<string, unknown>;
  config?: Record<string, unknown>;
  custom_config?: Record<string, unknown>;
  [key: string]: unknown;
}

interface IndustrySolutionApi {
  solution_key?: string;
  name?: string;
  description?: string;
  industry?: string;
  icon?: string;
  color?: string;
  dashboard_key?: string;
  item_types?: string[];
  features?: string[];
  use_cases?: string[];
  is_featured?: boolean;
  [key: string]: unknown;
}

interface SmartTaskConfigApiData {
  industry_solution?: IndustrySolutionApi | null;
  workflow_templates?: WorkflowTemplateApi[];
  settings?: Record<string, unknown> | null;
  workflow_modals?: Array<{ workflowKey: string; workflowName: string; modal: SmartTaskModalDefinition }>;
  shell_constants?: SmartTaskShellConstants | null;
  stat_cards_config?: SmartTaskStatCardsConfig | null;
  panel_smart_actions_config?: SmartTaskPanelSmartActionsConfig | null;
  display_modes_config?: SmartTaskDisplayModesConfig | null;
  unified_filter_tabs?: SmartTaskUnifiedFilterTab[] | null;
  [key: string]: unknown;
}

// ============================================================================
// SMARTTASK WORKFLOW MODAL CONFIG HOOK
// ============================================================================

function normalizeSmartTaskWorkflowKey(key: string): string {
  return key.trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
}

/**
 * Hook to access SmartTask workflow modal configurations.
 * Uses the server-provided modal registry from useSmartTaskConfig / API response.
 * Falls back to an empty list when no modals are supplied.
 */
export function useSmartTaskWorkflowModals(
  enabledTemplateKeys?: string[],
  allModals?: Array<{ workflowKey: string; workflowName: string; modal: SmartTaskModalDefinition }>,
): {
  allModals: Array<{ workflowKey: string; workflowName: string; modal: SmartTaskModalDefinition }>;
  getModalsForWorkflow: (workflowKey: string) => SmartTaskModalDefinition[];
  getModalDefinition: (workflowKey: string, modalKey: string) => SmartTaskModalDefinition | null;
  isLoading: boolean;
} {
  const isLoading = false;

  const normalizedAllModals = useMemo(() => allModals || [], [allModals]);

  const allModalsResult = useMemo(() => {
    const keys = (enabledTemplateKeys || []).map(normalizeSmartTaskWorkflowKey);
    if (keys.length === 0 || normalizedAllModals.length === 0) return [];

    return normalizedAllModals.filter((entry) => keys.includes(normalizeSmartTaskWorkflowKey(entry.workflowKey)));
  }, [enabledTemplateKeys, normalizedAllModals]);

  const getModalsForWorkflow = useCallback((workflowKey: string): SmartTaskModalDefinition[] => {
    const normalizedKey = normalizeSmartTaskWorkflowKey(workflowKey);
    const entry = normalizedAllModals.find((m) => normalizeSmartTaskWorkflowKey(m.workflowKey) === normalizedKey);
    return entry ? [entry.modal] : [];
  }, [normalizedAllModals]);

  const getModalDefinition = useCallback((workflowKey: string, modalKey: string): SmartTaskModalDefinition | null => {
    const normalizedKey = normalizeSmartTaskWorkflowKey(workflowKey);
    const entry = normalizedAllModals.find((m) => normalizeSmartTaskWorkflowKey(m.workflowKey) === normalizedKey);
    if (!entry) return null;
    return entry.modal.key === modalKey ? entry.modal : null;
  }, [normalizedAllModals]);

  return {
    allModals: allModalsResult,
    getModalsForWorkflow,
    getModalDefinition,
    isLoading,
  };
}

const SMARTTASK_CONFIG_FETCH_TIMEOUT_MS_WEB = 8_000;
const SMARTTASK_CONFIG_FETCH_TIMEOUT_MS_CAPACITOR = 12_000;
const SMARTTASK_MY_FEATURES_FETCH_TIMEOUT_MS_WEB = 5_000;
const SMARTTASK_MY_FEATURES_FETCH_TIMEOUT_MS_CAPACITOR = 12_000;

function shouldLogSmartTaskTimeoutDiagnostics(): boolean {
  if (typeof window === 'undefined') return false;
  if (!isCapacitor()) return true;
  return (window as unknown as Record<string, unknown>).__coldStartDiagnosticsEnabled === true;
}

function getSmartTaskFetchTimeoutMs(): number {
  if (typeof window === 'undefined') return SMARTTASK_CONFIG_FETCH_TIMEOUT_MS_WEB;
  return isCapacitor() ? SMARTTASK_CONFIG_FETCH_TIMEOUT_MS_CAPACITOR : SMARTTASK_CONFIG_FETCH_TIMEOUT_MS_WEB;
}

function getSmartTaskMyFeaturesTimeoutMs(): number {
  if (typeof window === 'undefined') return SMARTTASK_MY_FEATURES_FETCH_TIMEOUT_MS_WEB;
  return isCapacitor()
    ? SMARTTASK_MY_FEATURES_FETCH_TIMEOUT_MS_CAPACITOR
    : SMARTTASK_MY_FEATURES_FETCH_TIMEOUT_MS_WEB;
}

function writeSmartTaskConfigColdStartLog(tag: string, msg: string, data?: Record<string, unknown>): void {
  writeSmartTaskColdStartLog(tag, msg, data);
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit,
  timeoutMs = getSmartTaskFetchTimeoutMs(),
): Promise<Response | null> {
  const effectiveMs = Math.max(1_000, timeoutMs);
  const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = abortController
    ? window.setTimeout(() => {
      try {
        abortController.abort(
          new DOMException(`Smart Task config request exceeded ${effectiveMs}ms`, 'TimeoutError'),
        );
      } catch {
        abortController.abort();
      }
    }, effectiveMs)
    : null;

  try {
    return await fetch(input, {
      ...init,
      ...(abortController ? { signal: abortController.signal } : {}),
    });
  } catch (error) {
    const name = error instanceof Error ? error.name : '';
    const isTimeoutLike = name === 'AbortError' || name === 'TimeoutError';
    if (isTimeoutLike) {
      if (shouldLogSmartTaskTimeoutDiagnostics()) {
        logger.debug('[SmartTaskConfig] Timed or aborted request (offline/cache path may apply):', String(input), error);
      }
    } else if (!isSilentFetchError(error)) {
      logger.warn('[SmartTaskConfig] Timed or failed request:', input, error);
    }
    writeSmartTaskConfigColdStartLog('CONFIG_REQUEST_FAIL', 'Smart Task config request failed', {
      input: String(input),
      timeoutMs: effectiveMs,
      error: error instanceof Error ? error.message : String(error),
      timeoutLike: isTimeoutLike,
    });
    return null;
  } finally {
    if (timeoutId != null) {
      window.clearTimeout(timeoutId);
    }
  }
}

function useOptionalOperations() {
  try {
    return useOperations();
  } catch {
    return undefined;
  }
}

function hasExplicitSmartTaskTemplateSelection(settings?: Record<string, unknown> | null): boolean {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(settings, 'smart_task_template_keys')
    || Object.prototype.hasOwnProperty.call(settings, 'smart_task_template_key');
}

function buildDisabledWorkflowTemplateConfig(solutionName?: string | null): Record<string, unknown> {
  return {
    template_key: null,
    template_name: solutionName || 'Smart Task',
    allowed_item_types: [],
    document_types: [],
    quick_actions: [],
    begin_action: {
      sections: [],
      panel_actions: [],
      start_actions: [],
      workflow_sections: [],
    },
  };
}

function isSmartTaskSolutionKey(solutionKey?: string | null): solutionKey is string {
  return isStandardOperationsSolutionKey(solutionKey);
}

function getPreferredDemoSourceSolution(
  sourceConfiguration: DemoSourceConfiguration | null | undefined,
  preferredSolutionKey?: string | null,
): DemoSourceIndustrySolution | null {
  const solutions = (sourceConfiguration?.solutions || []).filter((solution) => isSmartTaskSolutionKey(solution.solution_key));
  if (solutions.length === 0) {
    return null;
  }

  const preferredKeys = [
    preferredSolutionKey,
    sourceConfiguration?.selected_solution_key,
    sourceConfiguration?.client_active_solution,
    sourceConfiguration?.active_solution_keys?.[0] || null,
  ].filter((value): value is string => isSmartTaskSolutionKey(value));

  for (const solutionKey of preferredKeys) {
    const match = solutions.find((solution) => solution.solution_key === solutionKey);
    if (match) {
      return match;
    }
  }

  return solutions[0] || null;
}

function normalizeDemoTemplateToApiTemplate(template: DemoSourceWorkflowTemplate): Record<string, unknown> {
  const baseConfig = template.config && typeof template.config === 'object' ? template.config as Record<string, unknown> : {};
  const customConfig = template.custom_config && typeof template.custom_config === 'object' ? template.custom_config as Record<string, unknown> : {};
  const smartTaskConfig = {
    ...((baseConfig.smart_task as Record<string, unknown>) || {}),
    ...((customConfig.smart_task as Record<string, unknown>) || {}),
  };

  return {
    template_key: template.template_key,
    name: template.template_name,
    template_name: template.template_name,
    description: template.description,
    category: template.category,
    icon: template.icon,
    color: template.color,
    item_type: template.applies_to_item_type,
    smart_task: smartTaskConfig,
    config: {
      ...baseConfig,
      ...customConfig,
      smart_task: smartTaskConfig,
    },
  };
}

function buildApiDataFromDemoSourceConfiguration(
  sourceConfiguration: DemoSourceConfiguration,
  solutionKeyHint?: string | null,
): Record<string, unknown> | null {
  const selectedSolution = getPreferredDemoSourceSolution(sourceConfiguration, solutionKeyHint);
  if (!selectedSolution) {
    return null;
  }

  const sourceTemplates = selectedSolution.templates || [];
  const activeTemplates = sourceTemplates.some((template) => template.is_active)
    ? sourceTemplates.filter((template) => template.is_active)
    : sourceTemplates;

  return {
    industry_solution: {
      solution_key: selectedSolution.solution_key,
      name: selectedSolution.name,
      description: selectedSolution.description,
      industry: selectedSolution.industry,
      icon: selectedSolution.icon,
      color: selectedSolution.color,
      dashboard_key: selectedSolution.dashboard_key,
      item_types: selectedSolution.item_types,
      features: selectedSolution.features,
      use_cases: selectedSolution.use_cases,
      is_featured: selectedSolution.is_featured,
    },
    workflow_templates: activeTemplates.map(normalizeDemoTemplateToApiTemplate),
    settings: {
      dashboard_key: selectedSolution.dashboard_key || sourceConfiguration.dashboard_key || null,
      solution_item_types: selectedSolution.item_types || sourceConfiguration.solution_item_types || [],
      solution_template_order: sourceConfiguration.solution_template_order || [],
      active_solution_keys: sourceConfiguration.active_solution_keys || [],
      ...(selectedSolution.smart_task_settings || {}),
    },
  };
}

async function loadSmartTaskDemoSession(): Promise<DemoSessionInfo | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const moduleKey = resolveOperationsDemoModuleKey(window.location.pathname);
    const { ok, data } = await fetchDemoSessionResponse(moduleKey, {
      includeSourceConfiguration: true,
    });
    if (!ok || !data.success || !data.session?.is_demo_mode) {
      return null;
    }

    return data.session;
  } catch (error) {
    if (!isSilentFetchError(error)) logger.warn('[SmartTaskConfig] Failed to load demo session context:', error);
    return null;
  }
}

interface UseSmartTaskConfigResult {
  config: SmartTaskConfig | null;
  isLoading: boolean;
  error: string | null;
  solutionOptions: SmartTaskSolutionOption[];
  activeSolutionKey: string | null;
  setActiveSolution: (solutionKey: string) => void;
  isLoadingSolutions: boolean;
  refetch: () => Promise<void>;
}

interface UseSmartTaskConfigOptions {
  bootstrapResolvedConfig?: SmartTaskConfig | null;
}

// Module-level config cache — survives component unmount/remount (but not full page refresh).
// This lets SmartTask render instantly from the last resolved config when re-opened,
// while the live fetch continues in the background.
let _cachedResolvedConfig: SmartTaskConfig | null = null;

export function useSmartTaskConfig(
  enabled: boolean = true,
  options: UseSmartTaskConfigOptions = {},
): UseSmartTaskConfigResult {
  const bootstrapResolvedConfig = options.bootstrapResolvedConfig || null;
  const initialResolvedConfig = enabled ? (bootstrapResolvedConfig || _cachedResolvedConfig) : null;
  const [config, setConfig] = useState<SmartTaskConfig | null>(initialResolvedConfig);
  const [isLoading, setIsLoading] = useState(enabled && !initialResolvedConfig);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  /** Latest config for fetchConfig closure — avoids re-showing full-screen loading on Operations-driven refetches */
  const configRef = useRef<SmartTaskConfig | null>(initialResolvedConfig);
  configRef.current = config;

  useEffect(() => {
    if (!bootstrapResolvedConfig) {
      return;
    }

    _cachedResolvedConfig = bootstrapResolvedConfig;
    setConfig((prev) => prev || bootstrapResolvedConfig);
    setIsLoading(false);
  }, [bootstrapResolvedConfig]);

  // Keep module-level cache in sync so subsequent mounts start instantly
  useEffect(() => {
    if (config) { _cachedResolvedConfig = config; }
  }, [config]);

  // IAM Consolidation: Use the full effective permissions object as the
  // authoritative permission source. The effective permissions endpoint
  // resolves role_definitions + custom_permissions + admin bypass, so
  // effectivePermissions.permissions contains the correct values for all
  // defined permission keys. We still fetch my-features for config-specific
  // data (template keys, display mode, industry solution) that is not
  // permission data.
  const effectivePermissions = useEffectivePermissions('total-operations');
  const effectivePermissionsRef = useRef<Record<string, unknown> | null>(null);
  effectivePermissionsRef.current = effectivePermissions.isLoading
    ? effectivePermissionsRef.current
    : effectivePermissions.permissions;
  // Provenance for each resolved key — lets the config resolver tell an
  // explicitly-set IAM value (group policy / user override) apart from a
  // definition or role default, so explicit values can win over legacy
  // client_config.fleet_settings fallbacks.
  const effectivePermissionsViaRef = useRef<Record<string, string> | null>(null);
  effectivePermissionsViaRef.current = effectivePermissions.isLoading
    ? effectivePermissionsViaRef.current
    : effectivePermissions.resolvedVia;
  // Per-user override keys (module_access.custom_permissions) — these outrank
  // every group layer, so the solution-slice overlay must not overwrite them.
  const effectivePermissionsOverridesRef = useRef<Record<string, unknown> | null>(null);
  effectivePermissionsOverridesRef.current = effectivePermissions.isLoading
    ? effectivePermissionsOverridesRef.current
    : effectivePermissions.overridesOnly;
  // Per-solution feature rows — each industry-solution parent's own values.
  // The active solution's slice overlays the flat map so a shared workflow
  // (e.g. fleet-management under two solutions) resolves the picked
  // solution's settings instead of a cross-solution merge.
  const effectivePermissionsSolutionsRef = useRef<Record<string, Record<string, unknown>> | null>(null);
  effectivePermissionsSolutionsRef.current = effectivePermissions.isLoading
    ? effectivePermissionsSolutionsRef.current
    : effectivePermissions.solutionPermissions;

  const operationsContext = useOptionalOperations();
  const activeSolutionTeam = operationsContext?.activeSolutionTeam ?? null;
  const activeSolutionKey = operationsContext?.activeSolutionKey ?? null;
  const setActiveSolution = useCallback((solutionKey: string) => {
    operationsContext?.setActiveSolution?.(solutionKey);
  }, [operationsContext]);
  const isLoadingSolutions = operationsContext?.isLoadingSolutions ?? false;

  const smartTaskSolutionTeams = useMemo(
    () => (operationsContext?.userSolutionTeams || []).filter((team) => (
      isSmartTaskSolutionKey(team.solutionKey) && isSmartTaskEnabledForTeam(team)
    )),
    [operationsContext?.userSolutionTeams]
  );

  const effectiveSolutionTeam = useMemo(() => {
    if (
      activeSolutionTeam?.solutionKey
      && isSmartTaskSolutionKey(activeSolutionTeam.solutionKey)
      && isSmartTaskEnabledForTeam(activeSolutionTeam)
    ) {
      return activeSolutionTeam;
    }

    if (activeSolutionKey && isSmartTaskSolutionKey(activeSolutionKey)) {
      return smartTaskSolutionTeams.find((team) => team.solutionKey === activeSolutionKey) || null;
    }

    return smartTaskSolutionTeams[0] || null;
  }, [activeSolutionKey, activeSolutionTeam, smartTaskSolutionTeams]);

  const effectiveSolutionKey = effectiveSolutionTeam?.solutionKey
    || (isSmartTaskSolutionKey(activeSolutionKey) ? activeSolutionKey : null)
    || smartTaskSolutionTeams[0]?.solutionKey
    || null;

  const solutionOptions = useMemo<SmartTaskSolutionOption[]>(() => {
    const teamMap = new Map<string, SmartTaskSolutionOption>();

    for (const team of smartTaskSolutionTeams) {
      if (!team?.solutionKey || !isSmartTaskEnabledForTeam(team)) {
        continue;
      }

      if (teamMap.has(team.solutionKey)) {
        continue;
      }

      const smartTaskSettings = getOperationsTeamSmartTaskSettings(team);
      const templateAssignment = buildSmartTaskTemplateAssignment(
        smartTaskSettings.smart_task_template_keys
        || smartTaskSettings.smart_task_template_key
        || null
      );

      teamMap.set(team.solutionKey, {
        solutionKey: team.solutionKey,
        solutionName: team.solutionName,
        icon: team.icon || 'Layers',
        color: team.color || '#3b82f6',
        role: team.role || 'user',
        memberCount: team.memberCount,
        workflowTemplateKeys: templateAssignment.smart_task_template_keys,
        displayMode: typeof smartTaskSettings.smart_task_display_mode === 'string'
          ? normalizeSmartTaskDisplayMode(smartTaskSettings.smart_task_display_mode)
          : undefined,
      });
    }

    return Array.from(teamMap.values());
  }, [smartTaskSolutionTeams]);

  useEffect(() => {
    if (!effectiveSolutionKey) {
      return;
    }

    if (effectiveSolutionKey !== activeSolutionKey) {
      setActiveSolution(effectiveSolutionKey);
    }
  }, [activeSolutionKey, effectiveSolutionKey, setActiveSolution]);

  const fetchConfig = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const authUser = authService.getUser();
    const clientId = authService.getSessionClientId() ?? 0;
    const userId = Number(authUser?.userId || authUser?.id || 0);
    const activeCacheSolutionKey = effectiveSolutionKey || null;

    const buildBaseUserFeatures = (data: MyFeaturesApiResponse): Record<string, unknown> => {
      const baseFeatures = (data?.data || data?.feature_settings || {}) as Record<string, unknown>;
      const templateAssignment = buildSmartTaskTemplateAssignment(
        data?.smart_task_template_keys
        || data?.smart_task_template_key
        || (baseFeatures.smart_task_template_keys as string[] | undefined)
        || (baseFeatures.smart_task_template_key as string | undefined)
        || null
      );

      return {
        ...baseFeatures,
        ...templateAssignment,
        operations_role: data?.operations_role || baseFeatures.operations_role || 'user',
        smart_task_industry_solution: data?.smart_task_industry_solution || baseFeatures.smart_task_industry_solution || null,
      };
    };

    const applyActiveTeamOverrides = (baseUserFeatures: Record<string, unknown> | null): Record<string, unknown> | null => {
      let nextUserFeatures = baseUserFeatures ? { ...baseUserFeatures } : null;

      const teamSmartTaskSettings = getOperationsTeamSmartTaskSettings(effectiveSolutionTeam);
      const activeTeamAllowsAppointmentDetours = teamSmartTaskSettings.allow_appointment_detours === true;
      const anySmartTaskSolutionTeamAllowsAppointmentDetours = smartTaskSolutionTeams.some((team) => {
        const smartTaskSettings = getOperationsTeamSmartTaskSettings(team);
        return smartTaskSettings.allow_appointment_detours === true;
      });
      const teamSolutionScopedOverrides = [
        'smart_task_template_keys',
        'smart_task_template_key',
        'smart_task_display_mode',
        'enable_parts_lookup',
        'enable_customer_history',
        'enable_photo_capture',
        'enable_signature_capture',
        'enable_checklist_forms',
        'enable_location_tagging',
        'enable_navigation',
        'enable_qr_scanning',
        'enable_barcode_scanning',
        'enable_voice_notes',
        'enable_offline_mode',
        'can_view_inventory',
        'view_job_financials',
        'view_customer_details',
        'can_create_jobs',
      ].reduce<Record<string, unknown>>((acc, key) => {
        if (Object.prototype.hasOwnProperty.call(teamSmartTaskSettings, key)) {
          acc[key] = teamSmartTaskSettings[key];
        }
        return acc;
      }, {});

      if (Object.keys(teamSolutionScopedOverrides).length > 0) {
        nextUserFeatures = { ...(nextUserFeatures || {}), ...teamSolutionScopedOverrides };
      }

      nextUserFeatures = {
        ...(nextUserFeatures || {}),
        allow_appointment_detours: activeTeamAllowsAppointmentDetours || anySmartTaskSolutionTeamAllowsAppointmentDetours,
      };

      if (nextUserFeatures) {
        const templateAssignment = buildSmartTaskTemplateAssignment(
          nextUserFeatures.smart_task_template_keys
          || nextUserFeatures.smart_task_template_key
          || null
        );

        nextUserFeatures = {
          ...nextUserFeatures,
          ...templateAssignment,
          operations_role: effectiveSolutionTeam?.role || nextUserFeatures.operations_role || 'user',
          smart_task_industry_solution: effectiveSolutionTeam?.solutionKey
            || nextUserFeatures.smart_task_industry_solution
            || null,
        };
      }

      if (nextUserFeatures && smartTaskSolutionTeams.length >= 2) {
        const modes = smartTaskSolutionTeams.map((team) => (
          normalizeSmartTaskDisplayMode(getOperationsTeamSmartTaskSettings(team).smart_task_display_mode)
        ));
        const uniqueModes = new Set(modes);
        if (uniqueModes.size > 1) {
          nextUserFeatures = {
            ...nextUserFeatures,
            smart_task_display_mode: normalizeSmartTaskDisplayMode('unified'),
          };
        }
      }

      return nextUserFeatures;
    };

    const resolveFromConfigBundle = ({
      apiData,
      userFeatures,
      resolvedVia,
      solutionKeyHint,
    }: {
      apiData: SmartTaskConfigApiData;
      userFeatures: Record<string, unknown> | null;
      resolvedVia?: Record<string, string> | null;
      solutionKeyHint?: string | null;
    }): { resolved: SmartTaskConfig; solutionKeyForCache: string | null } => {
      let templateConfig: Record<string, unknown> | Array<Record<string, unknown>> | null = null;
      const industrySolution = apiData?.industry_solution || null;
      const clientSettings = apiData?.settings || null;
      const allTemplates: WorkflowTemplateApi[] = apiData?.workflow_templates || [];

      const toResolvedTemplateConfig = (template: WorkflowTemplateApi) => ({
        template_key: template.template_key,
        template_name: template.name || template.template_name,
        solution_key: template.solution_key || null,
        icon: template.icon || null,
        color: template.color || null,
        item_type: template.item_type,
        document_types: template.document_types,
        workflow_hierarchy: template.hierarchy,
        workflow_statuses: template.statuses,
        ...((template.smart_task || {})),
        ...(clientSettings || {}),
      });

      let matchedTemplates: WorkflowTemplateApi[] = [];

      // Use all templates returned by the API — the API now aggregates across
      // all active_solution_keys, so every template here is relevant to the user.
      // Do NOT filter by smart_task_template_keys; that was causing modals from
      // secondary active solutions to be hidden.
      if (allTemplates.length > 0) {
        const smartTaskTemplates = allTemplates.filter((template: WorkflowTemplateApi) => {
          const smartTaskConfig = template.smart_task;
          return smartTaskConfig && typeof smartTaskConfig === 'object' && Object.keys(smartTaskConfig).length > 0;
        });

        matchedTemplates = smartTaskTemplates.length > 0 ? smartTaskTemplates : allTemplates;
      }

      if (matchedTemplates.length > 1) {
        templateConfig = matchedTemplates.map(toResolvedTemplateConfig);
      } else if (matchedTemplates.length === 1) {
        templateConfig = toResolvedTemplateConfig(matchedTemplates[0]);
      } else if (!templateConfig && clientSettings) {
        templateConfig = clientSettings;
      }

      if (industrySolution?.solution_key) {
        // Per-solution terminology: each workflow template resolves its own
        // industry's terms (a shared workflow under field-services must not be
        // relabelled by an unrelated active solution like security-services).
        const termsFor = (solutionKey: unknown) => getIndustryTerminology(
          (typeof solutionKey === 'string' && solutionKey.trim())
            || industrySolution.solution_key
            || '',
        );
        const applyTerms = (template: Record<string, unknown>) => ({
          ...template,
          terminology: {
            ...((template.terminology as Record<string, unknown>) || {}),
            ...termsFor(template.solution_key),
          },
        });
        if (Array.isArray(templateConfig)) {
          templateConfig = templateConfig.map(applyTerms);
        } else if (templateConfig) {
          templateConfig = applyTerms(templateConfig);
        } else {
          templateConfig = { terminology: termsFor(industrySolution.solution_key) };
        }
      }

      const resolved = resolveConfig(templateConfig, userFeatures, industrySolution as { solution_key: string; name: string; item_types: string[] } | null, resolvedVia);
      resolved.workflowModals = Array.isArray(apiData?.workflow_modals)
        ? apiData.workflow_modals
        : [];
      resolved.shellConstants = apiData?.shell_constants || null;
      resolved.statCardsConfig = apiData?.stat_cards_config || null;
      resolved.panelSmartActionsConfig = apiData?.panel_smart_actions_config || null;
      resolved.displayModesConfig = apiData?.display_modes_config || null;
      resolved.unifiedFilterTabs = Array.isArray(apiData?.unified_filter_tabs)
        ? apiData.unified_filter_tabs
        : [];

      return {
        resolved,
        solutionKeyForCache: industrySolution?.solution_key || solutionKeyHint || activeCacheSolutionKey,
      };
    };

    const hydrateCachedConfig = async (solutionKey?: string | null): Promise<boolean> => {
      if (!clientId || !userId || !solutionKey) return false;

      const snapshot = await getSmartTaskResolvedConfigSnapshot(clientId, userId, solutionKey);
      if (!snapshot?.resolvedConfig || requestId !== requestIdRef.current) {
        return false;
      }

      setConfig(snapshot.resolvedConfig as unknown as SmartTaskConfig);
      setError(null);
      return true;
    };

    const hydrateCachedRawBundle = async (
      solutionKey?: string | null,
      baseUserFeatures?: Record<string, unknown> | null,
    ): Promise<boolean> => {
      if (!clientId || !userId) return false;

      const [bundleSnapshot, featuresSnapshot] = await Promise.all([
        getSmartTaskRawConfigBundleSnapshot(clientId, userId, solutionKey || activeCacheSolutionKey),
        baseUserFeatures ? Promise.resolve(null) : getSmartTaskUserFeaturesSnapshot(clientId, userId),
      ]);

      if (!bundleSnapshot?.rawConfigBundle || requestId !== requestIdRef.current) {
        return false;
      }

      // IAM Consolidation: effective permissions are the base (sole permission
      // source). Cached userFeatures only contribute config-specific keys.
      const cachedRecord = baseUserFeatures || featuresSnapshot?.userFeatures || null;
      const configKeys = cachedRecord ? extractConfigKeysFromRecord(cachedRecord) : {};
      const teamOverrides = applyActiveTeamOverrides(configKeys);
      const iamPermissions = effectivePermissionsRef.current;
      const solutionSlice = effectiveSolutionKey
        ? effectivePermissionsSolutionsRef.current?.[effectiveSolutionKey] || null
        : null;
      const hydratedWithPermissions = iamPermissions && Object.keys(iamPermissions).length > 0
        ? { ...overlaySolutionPermissions(iamPermissions, solutionSlice, { overridesOnly: effectivePermissionsOverridesRef.current }), ...(teamOverrides || configKeys) }
        : (teamOverrides || configKeys || {});
      const { resolved } = resolveFromConfigBundle({
        apiData: bundleSnapshot.rawConfigBundle as unknown as SmartTaskConfigApiData,
        userFeatures: hydratedWithPermissions,
        resolvedVia: iamPermissions ? effectivePermissionsViaRef.current : null,
        solutionKeyHint: bundleSnapshot.solutionKey,
      });

      if (requestId !== requestIdRef.current) return false;

      setConfig(resolved);
      setError(null);
      return true;
    };

    const hydrateOfflineConfig = async (
      solutionKey?: string | null,
      baseUserFeatures?: Record<string, unknown> | null,
    ): Promise<boolean> => {
      const restoredFromBundle = await hydrateCachedRawBundle(solutionKey, baseUserFeatures);
      if (restoredFromBundle) return true;
      return hydrateCachedConfig(solutionKey || activeCacheSolutionKey);
    };

    try {
      // Only block the Smart Task shell when we have nothing to render yet. Refetches driven by
      // OperationsContext (effectiveSolutionKey / teams) must not reset to a global spinner — that
      // recreated SmartClock-style instant open after cache hydrate.
      if (!configRef.current && !_cachedResolvedConfig) {
        setIsLoading(true);
      }
      setError(null);
      writeSmartTaskConfigColdStartLog('CONFIG_START', 'Starting Smart Task config fetch', {
        activeSolutionKey: effectiveSolutionKey || undefined,
        hasCachedConfig: Boolean(_cachedResolvedConfig),
      });

      const token = authService.getTokens()?.access_token;
      let baseUserFeatures: Record<string, unknown> | null = null;

      const networkTimeoutMs = getSmartTaskFetchTimeoutMs();
      const myFeaturesTimeoutMs = getSmartTaskMyFeaturesTimeoutMs();
      const initialIndustrySolutionLock = effectiveSolutionKey || activeCacheSolutionKey || null;
      // Do NOT pass solution_key as a query param — the API now reads active_solution_keys
      // from client_industry_configs and aggregates templates across all active solutions.
      // Passing solution_key would override and limit to a single solution.
      const initialConfigUrl = getApiUrl('/api/v1/work-items/smart-task-config');

      const headers: HeadersInit | undefined = token
        ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
        : undefined;

      // Start network immediately — do not wait for encrypted offline cache reads (previously
      // getSmartTaskUserFeaturesSnapshot + hydrate ran sequentially before Promise.all, doubling cold-start latency on Capacitor).
      const networkPromise = token && headers
        ? Promise.all([
          fetchOperationsMyFeatures(token, { timeoutMs: myFeaturesTimeoutMs }),
          fetchWithTimeout(initialConfigUrl, { headers }, networkTimeoutMs),
          loadSmartTaskDemoSession(),
        ])
        : null;

      // After clear storage there is no encrypted Smart Task bundle — hydrate returns false and the UI
      // would otherwise wait for the full network stack (often 30–90s on device). Paint a safe shell
      // immediately from defaults + solution key; live API replaces this when it lands.
      if (
        token
        && networkPromise
        && initialIndustrySolutionLock
        && !configRef.current
        && !_cachedResolvedConfig
      ) {
        try {
          const placeholderIndustry = {
            solution_key: initialIndustrySolutionLock,
            name: 'Smart Task',
            item_types: [] as string[],
          };
          const placeholderConfig = resolveConfig(
            null,
            applyActiveTeamOverrides(null),
            placeholderIndustry,
          );
          if (requestId === requestIdRef.current) {
            setConfig(placeholderConfig);
            setIsLoading(false);
            writeSmartTaskConfigColdStartLog(
              'CONFIG_PLACEHOLDER',
              'Shell placeholder until API/cache hydrate completes (cold storage / clear data path)',
              { solutionKey: initialIndustrySolutionLock },
            );
          }
        } catch (placeholderError) {
          logger.warn('[SmartTaskConfig] Placeholder config failed:', placeholderError);
        }
      }

      if (!token) {
        const restored = await hydrateOfflineConfig(activeCacheSolutionKey, null);
        writeSmartTaskConfigColdStartLog('CONFIG_NO_TOKEN', 'No auth token during Smart Task config fetch', {
          restored,
          activeCacheSolutionKey: activeCacheSolutionKey || undefined,
        });
        if (!restored && requestId === requestIdRef.current) {
          setError('Not authenticated');
        }
        return;
      }

      // Run encrypted offline hydrate in parallel with my-features + smart-task-config + demo session.
      // Waiting on hydrate before starting fetch was a major Capacitor cold-start regression (single bridge / queue).
      const hydratePromise =
        clientId > 0 && userId > 0
          ? hydrateOfflineConfig(activeCacheSolutionKey, null)
          : Promise.resolve(false);

      // Critical: do NOT tie `isLoading` / shell unblock to the network leg. Hydrate often finishes in
      // tens of ms while my-features + smart-task-config can take 30s+ on device; awaiting Promise.all
      // before setIsLoading(false) recreated a multi-minute "config-loading" gate even after CONFIG_CACHE.
      void hydratePromise.then((cachedOk) => {
        if (cachedOk && requestId === requestIdRef.current) {
          setIsLoading(false);
          writeSmartTaskConfigColdStartLog('CONFIG_CACHE', 'Hydrated Smart Task config from cache while live fetch continues', {
            activeCacheSolutionKey: activeCacheSolutionKey || undefined,
          });
        }
      });

      const [cachedOk, networkTriple] = await Promise.all([hydratePromise, networkPromise!]);

      const [myFeaturesData, initialConfigRes, demoSession] = networkTriple;

      const demoSourceConfiguration = demoSession?.source_configuration || null;
      const demoPreferredSolution = getPreferredDemoSourceSolution(
        demoSourceConfiguration,
        effectiveSolutionKey,
      );

      if (myFeaturesData?.success) {
        baseUserFeatures = buildBaseUserFeatures(myFeaturesData as MyFeaturesApiResponse);
        if (clientId > 0 && userId > 0) {
          void saveSmartTaskUserFeaturesSnapshot({
            clientId,
            userId,
            userFeatures: baseUserFeatures,
          });
        }
      }

      // IAM Consolidation: effective permissions are the BASE (sole permission
      // source). my-features only contributes config-specific keys (template
      // keys, display mode, industry solution, operations_role) — NOT permission
      // values. Team overrides may further adjust config keys.
      //
      // Merge order (lowest → highest priority):
      //   1. effectivePermissions.permissions  (all permission keys)
      //   2. config keys from my-features      (template, display mode, etc.)
      //   3. team overrides                     (team-scoped config + role)
      const iamPermissions = effectivePermissionsRef.current;
      const solutionSlice = effectiveSolutionKey
        ? effectivePermissionsSolutionsRef.current?.[effectiveSolutionKey] || null
        : null;
      const configKeys = myFeaturesData?.success
        ? extractConfigKeys(myFeaturesData as MyFeaturesApiResponse)
        : (baseUserFeatures ? extractConfigKeysFromRecord(baseUserFeatures) : {});
      const teamOverrides = applyActiveTeamOverrides(configKeys);
      const effectiveUserFeatures = iamPermissions && Object.keys(iamPermissions).length > 0
        ? { ...overlaySolutionPermissions(iamPermissions, solutionSlice, { overridesOnly: effectivePermissionsOverridesRef.current }), ...(teamOverrides || configKeys) }
        : (teamOverrides || configKeys || {});
      const demoPreferredSmartTaskSettings = demoPreferredSolution?.smart_task_settings || null;
      const demoTemplateAssignment = buildSmartTaskTemplateAssignment(
        demoPreferredSmartTaskSettings?.smart_task_template_keys
        || demoPreferredSmartTaskSettings?.smart_task_template_key
        || (demoPreferredSolution?.templates.some((template) => template.is_active)
          ? demoPreferredSolution?.templates.filter((template) => template.is_active)
          : demoPreferredSolution?.templates || []
        ).map((template) => template.template_key)
      );
      const demoUserFeatures = demoSession?.is_demo_mode
        ? {
          ...(effectiveUserFeatures || {}),
          ...(demoPreferredSmartTaskSettings || {}),
          ...(demoPreferredSolution?.solution_key
            ? { smart_task_industry_solution: demoPreferredSolution.solution_key }
            : {}),
          ...(demoTemplateAssignment.smart_task_template_keys.length > 0 || demoTemplateAssignment.smart_task_template_key
            ? demoTemplateAssignment
            : {}),
        }
        : effectiveUserFeatures;

      const demoApiData = demoSourceConfiguration
        ? buildApiDataFromDemoSourceConfiguration(
          demoSourceConfiguration,
          effectiveSolutionKey || demoPreferredSolution?.solution_key || null,
        )
        : null;

      if (demoSession?.is_demo_mode && demoApiData) {
        const { resolved, solutionKeyForCache } = resolveFromConfigBundle({
          apiData: demoApiData as unknown as SmartTaskConfigApiData,
          userFeatures: demoUserFeatures,
          resolvedVia: iamPermissions ? effectivePermissionsViaRef.current : null,
          solutionKeyHint: demoPreferredSolution?.solution_key || activeCacheSolutionKey,
        });

        if (requestId !== requestIdRef.current) return;
        setConfig(resolved);
        writeSmartTaskConfigColdStartLog('CONFIG_DEMO', 'Resolved Smart Task config from demo session', {
          solutionKeyForCache: solutionKeyForCache || undefined,
        });

        if (clientId > 0 && userId > 0 && solutionKeyForCache) {
          void saveSmartTaskRawConfigBundleSnapshot({
            clientId,
            userId,
            solutionKey: solutionKeyForCache,
            rawConfigBundle: demoApiData,
          });

          void saveSmartTaskResolvedConfigSnapshot({
            clientId,
            userId,
            solutionKey: solutionKeyForCache,
            resolvedConfig: resolved as unknown as Record<string, unknown>,
          });
        }

        return;
      }

      // Team-selected solution is now the primary scope for SmartTask configuration.
      const industrySolutionLock = effectiveSolutionKey
        || (demoUserFeatures?.smart_task_industry_solution as string | undefined)
        || null;

      const configRes = industrySolutionLock === initialIndustrySolutionLock
        ? initialConfigRes
        : await fetchWithTimeout(
          getApiUrl('/api/v1/work-items/smart-task-config'),
          { headers },
          networkTimeoutMs,
        );

      if (!configRes?.ok) {
        const restored = await hydrateOfflineConfig(industrySolutionLock || activeCacheSolutionKey, baseUserFeatures);
        writeSmartTaskConfigColdStartLog('CONFIG_FALLBACK', 'Smart Task config request unavailable, used fallback path', {
          restored,
          industrySolutionLock: industrySolutionLock || undefined,
          activeCacheSolutionKey: activeCacheSolutionKey || undefined,
        });
        if (!restored && requestId === requestIdRef.current) {
          setError('Failed to load configuration');
          // Keep an already-resolved config (module cache / earlier hydrate /
          // prior successful fetch): replacing it with bare defaults drops
          // workflowTemplates + workflowModals, which hides every workflow
          // tile in BeginActionModal until the next successful fetch.
          if (!configRef.current && !_cachedResolvedConfig) {
            setConfig(resolveConfig(null, demoUserFeatures, null));
          }
        }
        return;
      }

      const rawData = await configRes.json() as Record<string, unknown>;
      // API returns { success: true, data: { industry_solution, workflow_templates[], settings, ... } }
      const apiData = (rawData.success ? (rawData.data || rawData) : rawData) as SmartTaskConfigApiData;

      const { resolved, solutionKeyForCache } = resolveFromConfigBundle({
        apiData,
        userFeatures: demoUserFeatures,
        resolvedVia: iamPermissions ? effectivePermissionsViaRef.current : null,
        solutionKeyHint: industrySolutionLock || activeCacheSolutionKey,
      });

      if (requestId !== requestIdRef.current) return;
      setConfig(resolved);
      writeSmartTaskConfigColdStartLog('CONFIG_READY', 'Resolved Smart Task config from live API', {
        solutionKeyForCache: solutionKeyForCache || undefined,
        industrySolutionLock: industrySolutionLock || undefined,
      });

      if (clientId > 0 && userId > 0 && solutionKeyForCache) {
        void saveSmartTaskRawConfigBundleSnapshot({
          clientId,
          userId,
          solutionKey: solutionKeyForCache,
          rawConfigBundle: apiData,
        });

        void saveSmartTaskResolvedConfigSnapshot({
          clientId,
          userId,
          solutionKey: solutionKeyForCache,
          resolvedConfig: resolved as unknown as Record<string, unknown>,
        });
      }
    } catch (err) {
      if (!isSilentFetchError(err)) logger.error('[SmartTaskConfig] Failed to load config:', err);
      writeSmartTaskConfigColdStartLog('CONFIG_ERROR', 'Smart Task config fetch failed with exception', {
        error: err instanceof Error ? err.message : String(err),
        activeCacheSolutionKey: activeCacheSolutionKey || undefined,
      });

      const restored = await hydrateOfflineConfig(activeCacheSolutionKey, null);
      if (requestId !== requestIdRef.current) return;
      if (restored) return;

      setError('Failed to load configuration');
      // Set defaults so UI can still render — but never clobber a
      // previously-resolved config on a transient failure (see note above).
      if (!configRef.current && !_cachedResolvedConfig) {
        setConfig(resolveConfig(null, null, null));
      }
    } finally {
      // Only the latest request should toggle isLoading to avoid stale writes.
      // However, always clear isLoading for stale requests that already have a
      // config, because the UI gate is `configLoading && !config` — and leaving
      // isLoading stuck with a valid config still blocks secondary spinners.
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      } else if (_cachedResolvedConfig) {
        setIsLoading(false);
      }
    }
  }, [effectiveSolutionKey, effectiveSolutionTeam, smartTaskSolutionTeams]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    fetchConfig();
  }, [fetchConfig, enabled]);

  // IAM Consolidation: Re-resolve config when effective permissions arrive
  // from the IAM endpoint. The initial fetchConfig runs without waiting for
  // permissions (preserving cold-start performance); when permissions become
  // available, we trigger a refetch so they're merged into the resolved config.
  // effectivePermissions.permissions is in the deps so the config also
  // re-resolves when permissions CHANGE after load (e.g. an admin toggles a
  // group policy — the IAM_PERMISSIONS_CHANGED_EVENT refetch produces a new
  // permissions object, and the resolved rules/features must follow it in
  // realtime). A boolean flag alone would only fire on the first load.
  const iamPermissionsLoaded = !effectivePermissions.isLoading
    && Object.keys(effectivePermissions.permissions).length > 0;
  useEffect(() => {
    if (!enabled || !iamPermissionsLoaded) {
      return;
    }
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- iamPermissionsLoaded is a boolean derived flag; fetchConfig is stable per deps
  }, [iamPermissionsLoaded, enabled, effectivePermissions.permissions]);

  return {
    config,
    isLoading,
    error,
    solutionOptions,
    activeSolutionKey: effectiveSolutionKey,
    setActiveSolution,
    isLoadingSolutions,
    refetch: fetchConfig,
  };
}
